import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaExclamationTriangle, FaUserMd, FaFlask, FaSyringe,
  FaPhone, FaArrowRight, FaShieldAlt, FaChartLine
} from 'react-icons/fa';

const LAST_UPDATED = 'March 1, 2026';

const GLP1MedicalDisclaimer = () => {
  return (
    <div className="bg-white min-h-screen">

      {/* Hero */}
      <div className="bg-gradient-to-br from-red-900 to-red-800 text-white py-14 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-white/10 rounded-2xl mb-4">
            <FaExclamationTriangle className="text-2xl text-yellow-400" />
          </div>
          <h1 className="text-4xl font-bold mb-3">Medical Disclaimer</h1>
          <p className="text-red-200">Fitnession GLP-1 Program · Last updated: {LAST_UPDATED}</p>
          <p className="text-red-300 text-sm mt-2">Please read this carefully before using our GLP-1 medical weight loss service</p>
        </div>
      </div>

      {/* Emergency Banner */}
      <div className="bg-red-600 text-white py-3 px-4 text-center">
        <p className="font-bold text-sm">
          🚨 MEDICAL EMERGENCY? Call <a href="tel:112" className="underline font-black text-base">112</a> or go to your nearest emergency room. Do NOT use this platform.
        </p>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-10">

        {/* 1 — Not a Substitute */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <FaUserMd className="text-red-600 text-lg" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Not a Substitute for Medical Advice</h2>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
            <p className="text-gray-700 leading-relaxed mb-4">
              The content on glp1.fitnession.fit — including articles, research summaries, FAQ answers, eligibility information, and AI-generated responses — is provided for <strong>general informational purposes only</strong>. It does not constitute:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Medical advice, diagnosis, or treatment</li>
              <li>A doctor-patient relationship (until a formal consultation is established)</li>
              <li>A guarantee of eligibility for GLP-1 medication</li>
              <li>A substitute for advice from your own physician, endocrinologist, or specialist</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              <strong>Always consult a qualified, licensed medical professional</strong> before starting, stopping, or changing any medication, diet, or treatment plan — including any guidance received through this platform.
            </p>
          </div>
        </section>

        {/* 2 — GLP-1 Prescription Only */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <FaSyringe className="text-orange-600 text-lg" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">GLP-1 Medications Are Prescription-Only</h2>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-2xl p-6 space-y-4">
            <p className="text-gray-700">
              GLP-1 receptor agonists (semaglutide, liraglutide, tirzepatide) are <strong>Schedule H prescription drugs in India</strong> regulated under the Drugs and Cosmetics Act 1940. This means:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>They can only be dispensed with a valid prescription from a licensed physician.</li>
              <li>Self-prescribing or obtaining these medications without a prescription is illegal.</li>
              <li>Sharing prescribed medication with another person is illegal and dangerous.</li>
              <li>Do not purchase GLP-1 medications from unverified online sources — counterfeit medication is a serious risk.</li>
            </ul>
            <p className="text-gray-700 font-medium">
              Through Fitnession, prescriptions are issued only by our licensed doctors after a full clinical assessment. Your medication is sourced from licensed Indian pharmacies with verified supply chains.
            </p>
          </div>
        </section>

        {/* 3 — Results Vary */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <FaChartLine className="text-yellow-600 text-lg" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Individual Results Vary</h2>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 space-y-4">
            <p className="text-gray-700">
              Weight loss results presented on this website — including statistics like "15–20% body weight loss in 6 months" — are <strong>population averages from controlled clinical trials</strong>. Your results may be significantly different. Factors affecting outcomes include:
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                'Starting weight and BMI',
                'Underlying health conditions (diabetes, thyroid, PCOD)',
                'Medication adherence and dosage reached',
                'Dietary and lifestyle changes made',
                'Individual metabolic rate',
                'Concurrent medications that may interact',
                'Genetic factors',
                'Duration of treatment',
              ].map((f, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="w-4 h-4 rounded-full bg-yellow-300 flex-shrink-0 mt-0.5" />
                  {f}
                </div>
              ))}
            </div>
            <p className="text-gray-700 text-sm">
              Testimonials and success stories on this website are real patient experiences. They are not typical results and are not a guarantee that you will achieve similar outcomes.
            </p>
          </div>
        </section>

        {/* 4 — Clinical Data Disclaimer */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <FaFlask className="text-blue-600 text-lg" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Clinical Research Data</h2>
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 space-y-3">
            <p className="text-gray-700">
              Clinical trial data referenced on our Research page (e.g., STEP trials, SURMOUNT trials, SCALE trials) is sourced from peer-reviewed publications. We summarise this data in plain language for patient education. You should note:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Trial results represent averages across diverse populations under controlled conditions.</li>
              <li>Real-world outcomes in clinical practice may differ from controlled trial results.</li>
              <li>Some medications referenced may have received approval after publication of the trials we cite.</li>
              <li>Medical science evolves — information may not reflect the most recent research.</li>
              <li>The information on our Research page is reviewed periodically but may not be updated in real time.</li>
            </ul>
            <p className="text-gray-700 text-sm">
              For the most current clinical evidence, consult peer-reviewed sources directly (PubMed, NEJM, The Lancet) or discuss with your doctor.
            </p>
          </div>
        </section>

        {/* 5 — Side Effects & Contraindications */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <FaShieldAlt className="text-red-600 text-lg" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Known Risks & Side Effects</h2>
          </div>
          <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <p className="font-semibold text-gray-800 mb-3">Common Side Effects (usually temporary)</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {[
                  'Nausea (44% of patients, decreases over time)',
                  'Diarrhea (30%)',
                  'Constipation (24%)',
                  'Vomiting (24%)',
                  'Decreased appetite',
                  'Injection site reactions',
                  'Fatigue (especially early)',
                  'Headache',
                ].map((s, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0 mt-1.5" />
                    {s}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-red-200 rounded-2xl p-6">
              <p className="font-semibold text-red-800 mb-3">Serious Risks (rare — require immediate medical attention)</p>
              <div className="space-y-2">
                {[
                  { risk: 'Pancreatitis', desc: 'Severe, persistent abdominal pain — stop medication and seek emergency care.' },
                  { risk: 'Thyroid tumours', desc: 'Boxed warning for medullary thyroid carcinoma. Do not use if personal/family history.' },
                  { risk: 'Gallbladder disease', desc: 'Rapid weight loss increases gallstone risk (1–2%).' },
                  { risk: 'Hypoglycaemia', desc: 'Risk increases if combined with insulin or sulfonylureas — requires dose adjustment.' },
                  { risk: 'Kidney injury', desc: 'Severe dehydration from vomiting/diarrhoea may worsen kidney function.' },
                  { risk: 'Allergic reaction', desc: 'Rare anaphylaxis — seek emergency care immediately.' },
                ].map((r, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0 mt-2" />
                    <div className="text-sm">
                      <span className="font-semibold text-red-800">{r.risk}: </span>
                      <span className="text-gray-600">{r.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <p className="font-semibold text-gray-800 mb-3">Absolute Contraindications — Do Not Use If:</p>
              <ul className="list-disc list-inside space-y-1.5 text-sm text-gray-600">
                <li>Personal or family history of medullary thyroid carcinoma or MEN2</li>
                <li>Pregnant, breastfeeding, or planning pregnancy within 6 months</li>
                <li>Type 1 diabetes</li>
                <li>History of pancreatitis</li>
                <li>Severe renal or hepatic impairment</li>
                <li>Active eating disorder</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 6 — Emergency Contacts */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <FaPhone className="text-white text-lg" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Emergency Contacts</h2>
          </div>
          <div className="bg-red-600 text-white rounded-2xl p-6 space-y-3">
            <p className="font-semibold text-lg mb-3">If you are experiencing a medical emergency:</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { label: 'National Emergency', number: '112', sub: 'Ambulance, Police, Fire' },
                { label: 'Ambulance', number: '102', sub: 'EMRI Ambulance service' },
                { label: 'Apollo Emergency', number: '1066', sub: '24×7 helpline' },
                { label: 'Poison Control', number: '1800-11-6117', sub: 'Medication poisoning' },
              ].map((c, i) => (
                <a key={i} href={`tel:${c.number.replace(/-/g, '')}`} className="bg-white/10 hover:bg-white/20 rounded-xl p-4 transition-colors">
                  <p className="text-xs text-red-200">{c.label}</p>
                  <p className="text-2xl font-black">{c.number}</p>
                  <p className="text-xs text-red-200 mt-0.5">{c.sub}</p>
                </a>
              ))}
            </div>
            <p className="text-red-200 text-sm mt-2">
              After stabilisation, notify your Fitnession doctor via the platform or WhatsApp: +91 9579520586
            </p>
          </div>
        </section>

        {/* 7 — Regulatory */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Regulatory Status</h2>
          <div className="bg-gray-50 rounded-2xl p-6">
            <p className="text-gray-600 mb-4">GLP-1 medications dispensed through this programme:</p>
            <div className="space-y-2 text-sm text-gray-700">
              {[
                ['Ozempic (Semaglutide)', 'Approved by USFDA (2017), EMA (2018), CDSCO India (2021) for Type 2 diabetes'],
                ['Wegovy (Semaglutide 2.4mg)', 'Approved by USFDA (2021) for chronic weight management; CDSCO approval pending for obesity indication'],
                ['Saxenda (Liraglutide 3mg)', 'Approved by USFDA (2014), EMA (2015), CDSCO India for chronic weight management'],
                ['Mounjaro (Tirzepatide)', 'Approved by USFDA (2022), EMA (2023) for Type 2 diabetes; available in India via licensed import'],
              ].map(([drug, status], i) => (
                <div key={i} className="bg-white rounded-xl p-4 border border-gray-100">
                  <p className="font-semibold text-gray-800">{drug}</p>
                  <p className="text-gray-500 mt-0.5">{status}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-4">Regulatory status is subject to change. Your doctor will confirm current approval status during consultation.</p>
          </div>
        </section>

        {/* CTA */}
        <div className="bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-8 text-white text-center">
          <p className="text-xl font-bold mb-2">Questions About Safety?</p>
          <p className="text-gray-300 text-sm mb-6">Our doctors are available to answer any medical questions before you commit to the programme.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/eligibility" className="inline-flex items-center justify-center bg-gold-500 hover:bg-gold-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors">
              Check Eligibility <FaArrowRight className="ml-2" />
            </Link>
            <a href="https://wa.me/919579520586" className="inline-flex items-center justify-center border border-white/30 text-white hover:bg-white/10 px-6 py-3 rounded-xl font-semibold transition-colors">
              Ask a Doctor on WhatsApp
            </a>
          </div>
        </div>

      </div>
    </div>
  );
};

export default GLP1MedicalDisclaimer;
